import { useEffect, useRef, useSyncExternalStore } from 'react'

/**
 * Whether the browser believes it has a connection (UI-2f: out of the lock
 * screen, for the shell's "Senza connessione" too). The server's answers have the
 * last word: a connection the browser believes in can still fail, and every
 * screen says so where it asked. `onBack` runs as the connection comes back.
 * UI-2g: read as React reads any store outside it, so a change between the
 * first render and the listeners is not missed; `onBack` is the one last
 * passed, so a new function at each render adds no listeners.
 */
export function useOnline(onBack?: () => void): boolean {
  const online = useSyncExternalStore(subscribe, browserOnline, serverOnline)
  const back = useRef(onBack)
  const was = useRef(online)
  useEffect(() => {
    back.current = onBack
  })
  useEffect(() => {
    if (online && !was.current) back.current?.()
    was.current = online
  }, [online])
  return online
}

function subscribe(changed: () => void): () => void {
  window.addEventListener('online', changed)
  window.addEventListener('offline', changed)
  return () => {
    window.removeEventListener('online', changed)
    window.removeEventListener('offline', changed)
  }
}

const browserOnline = () => navigator.onLine

/** No window to ask: connected, until an event says otherwise. */
const serverOnline = () => true
